/**
 * Flair sheet (master_sheet) read-only table. Uses AdminContext for data and column width.
 */
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useAdminContext } from './AdminContext';

export default function FlairSheetSection() {
  const {
    masterSheetData,
    isLoadingMasterSheet,
    subreddit,
    flairSheetCdnSub,
    CDN_BASE_URL,
    getColumnWidth,
  } = useAdminContext();
  const cdnSubForImages = flairSheetCdnSub || subreddit;

  if (isLoadingMasterSheet) {
    return <CircularProgress />;
  }

  if (!masterSheetData || masterSheetData.length === 0) {
    return <Alert severity="info">No data found in flair sheet.</Alert>;
  }

  const columns = Object.keys(masterSheetData[0]).filter((key) => key !== 'id');

  return (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight: '70vh',
        overflowX: 'auto',
        overflowY: 'auto',
      }}
    >
      <Table size="small" stickyHeader sx={{ tableLayout: 'auto' }}>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col}
                sx={{
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  ...(getColumnWidth ? getColumnWidth(col) : { minWidth: 100 }),
                }}
              >
                {col.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {masterSheetData.map((row: Record<string, unknown>, idx: number) => {
            const imageSource = (row['image_source'] || row['image_name'] || '') as string;
            const imageUrl =
              imageSource && cdnSubForImages
                ? (() => {
                    const cleanSource = imageSource.replace('.png', '').trim();
                    const imageName = cleanSource.includes('/') ? cleanSource.split('/').pop() : cleanSource;
                    return `${CDN_BASE_URL}/60x60/${cdnSubForImages}/${imageName}.png`;
                  })()
                : '';

            return (
              <TableRow key={idx} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                {columns.map((col) => {
                  const rawValue = row[col];
                  const cellValue = String(rawValue ?? '');
                  const booleanFields = [
                    'active_flair',
                    'active_image',
                    'default_flair',
                    'selectable_flair',
                    'selectable_inline',
                  ];
                  const isBooleanField = booleanFields.includes(col);
                  const boolValue = isBooleanField
                    ? (rawValue === 1 || rawValue === true || rawValue === '1')
                    : null;
                  const isImageColumn = col === 'image_name' || col === 'image_source';
                  const isTextColumn = typeof cellValue === 'string' && cellValue.length > 0;

                  const cellInnerContent = isBooleanField ? (
                    <Typography
                      variant="body2"
                      sx={{ color: boolValue ? 'success.main' : 'text.secondary' }}
                    >
                      {boolValue ? 'Yes' : 'No'}
                    </Typography>
                  ) : isImageColumn && imageUrl ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img
                        src={imageUrl}
                        alt={cellValue}
                        loading="lazy"
                        decoding="async"
                        style={{ width: 20, height: 20, objectFit: 'contain' }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      <span>{cellValue}</span>
                    </Box>
                  ) : (
                    cellValue
                  );

                  const colWidth = getColumnWidth ? getColumnWidth(col) : { minWidth: 100 };

                  return (
                    <TableCell
                      key={col}
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: colWidth.minWidth,
                        py: 0.5,
                        ...colWidth,
                      }}
                      title={
                        isTextColumn && !isBooleanField && cellValue.length > 30 ? cellValue : undefined
                      }
                    >
                      {cellInnerContent}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
